import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FieldPath, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  createCoachProfile,
  createCoachingResult,
  deleteCoachingResult,
  getCoach,
  updateCoach,
  updateCoachProfile,
  updateCoachingResult,
} from "~/lib/server/coach";
import { ControlledTextarea } from "~/components/form/controlled-inputs";
import { Feature, hasFeature } from "~/lib/has-feature";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { getDBWithInit } from "~/lib/db";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { CTASection } from "~/components/ui/cta-section";
import {
  TrackingCampaign,
  TrackingMedium,
} from "~/lib/monitoring/tracking-parameters";
import { ProfileEditHeader } from "../../../../../components/profile-edit-header";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { createNoIndexMeta } from "~/lib/shared/meta";
import {
  FormCheckboxField,
  FormFileInputField,
  FormIconPickerField,
  FormSelectField,
} from "~/components/form/form-fields";

const UrlSchema = z.union([z.literal(""), z.string().url()]);

const ProfileSchema = z.object({
  id: z.string(),
  coachId: z.string(),

  isPublic: z.boolean(),

  profilePictureUrl: z.string().url().nullable(),

  brandName: z.string().max(50).min(2).nullable().optional(),
  brandLogoUrl: UrlSchema.nullable().optional(),

  coachingTitle: z.string().max(50).min(3).nullable(),
  languages: z.string().nullable(),
  coachingStyle: z.string().nullable(),
  formOfConduct: z.string().nullable(),

  openingStatement: z.string().max(100).nullable(),
  heroImageSrc: UrlSchema.nullable().optional(),
  mainImage: UrlSchema.nullable().optional(),
  mainVideo: UrlSchema.nullable().optional(),

  title: z.string().max(150).min(3).nullable(),
  description: z.string().max(400).min(3).nullable(),

  whyTitle: z.string().max(100).nullable(),
  whyContent: z.string().max(500).nullable(),

  website: UrlSchema.nullable(),
  facebookProfile: UrlSchema.nullable(),
  linkedinProfile: UrlSchema.nullable(),
  instagramProfile: UrlSchema.nullable(),
  youtubeChannel: UrlSchema.nullable(),

  ctaButtonText: z.string().min(1).max(50).nullable(),

  results: z
    .array(
      z.object({
        id: z.string().optional(),
        _delete: z.boolean(),
        icon: z.string().min(1),
        title: z.string().min(1),
        description: z.string(),
        tempId: z.string().optional(),
      }),
    )
    .min(1, "Please add at least one result."),

  leadMagnetTitle: z.string().max(100).nullable(),
  leadMagnetDescription: z.string().max(500).nullable(),
  leadMagnetCtaText: z.string().max(50).nullable(),
  leadMagnetUrl: UrlSchema.nullable(),
});

type FormData = z.infer<typeof ProfileSchema>;

const getCoachProfile = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coachId = context.coach.id;

    if (!coachId) {
      throw new Error("Coach ID is required");
    }

    const fullCoach = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: (coaches, { eq }) => eq(coaches.id, coachId),
      with: {
        profile: true,
        results: true,
      },
    });

    if (!fullCoach) {
      throw new Error("Coach not found");
    }

    let profile;
    if (fullCoach.profile) {
      profile = fullCoach.profile;
    } else {
      console.warn("No profile found for coach, creating empty", fullCoach.id);
      profile = await createCoachProfile({
        coachId: fullCoach.id,
      });
      await updateCoach(fullCoach.id, { profileId: profile.id });
    }

    return {
      ...profile,
      profilePictureUrl: fullCoach.profilePictureUrl,
      results: fullCoach.results.map((r) => ({
        ...r,
        _delete: false,
        tempId: `existing-${r.id}`,
      })),
    };
  });

const updateCoachProfileAction = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(ProfileSchema)
  .handler(async ({ data }) => {
    // Update profile
    await updateCoachProfile(data.id, data);
    await updateCoach(data.coachId, {
      profilePictureUrl: data.profilePictureUrl,
      profile_manually_edited: true,
    });

    for (const r of data.results) {
      if (!r.id) {
        const { id: _id, _delete, tempId: _tempId, ...resultData } = r;
        await createCoachingResult({
          ...resultData,
          coachId: data.coachId,
        });
      } else if (r._delete) {
        await deleteCoachingResult(r.id);
      } else {
        const { id: _id, _delete, tempId: _tempId, ...resultData } = r;
        await updateCoachingResult(r.id, resultData);
      }
    }

    // Return updated data
    const coach = await getCoach(data.coachId);
    if (!coach) {
      throw new Error("Coach not found");
    }

    const fullCoach = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: (coaches, { eq }) => eq(coaches.id, coach.id),
      with: {
        profile: true,
        results: true,
      },
    });

    if (!fullCoach) {
      throw new Error("Coach not found");
    }

    return {
      ...fullCoach.profile!,
      profilePictureUrl: fullCoach.profilePictureUrl,
      results: fullCoach.results.map((r) => ({
        ...r,
        _delete: false,
        tempId: `existing-${r.id}`,
      })),
    };
  });

export const Route = createFileRoute("/coach/_backend/edit-profile/profile/")({
  head: () =>
    createNoIndexMeta(
      "Edit Profile – Dream Coach Match",
      "Edit your coach profile information and achievements",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["coach-profile"],
      queryFn: () => getCoachProfile(),
    });
  },
  component: ProfileEditPage,
});

function ProfileEditPage() {
  const queryClient = useQueryClient();
  const getCoachProfileFn = useServerFn(getCoachProfile);
  const coach = useCoachPrivateProfileRequired();

  const {
    data: profileData,
    isLoading: isLoadingProfileData,
    isError: isErrorProfileData,
  } = useQuery({
    queryKey: ["coach-profile"],
    queryFn: () => getCoachProfileFn(),
  });

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    control,
    reset,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: profileData,
  });

  const { errors, isDirty } = formState;

  // No reset-on-load: rely on defaultValues (like other working pages)

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => updateCoachProfileAction({ data }),
    onSuccess: async () => {
      toast.success("Profile updated successfully");

      // Invalidate broader coach cache to update dependent data
      await queryClient.invalidateQueries({ queryKey: ["coach"] });

      // Refetch fresh data to get new IDs and remove deleted items
      const freshData = await queryClient.fetchQuery({
        queryKey: ["coach-profile"],
        queryFn: () => getCoachProfileFn(),
      });
      reset(freshData);
    },
    onError: (error) => {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    },
  });

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && 0 < Object.keys(errors).length,
  );

  const results = watch("results") || [];

  const addResult = () => {
    const currentResults = getValues("results") || [];
    const newResult = {
      _delete: false,
      id: undefined,
      title: "",
      description: "",
      icon: "award",
      tempId: `temp-${Date.now()}-${Math.random()}`,
    };
    setValue("results", [...currentResults, newResult], {
      shouldDirty: true,
    });
  };

  const moveResult = (fromIndex: number, toIndex: number) => {
    const currentResults = getValues("results") || [];
    if (0 > toIndex || toIndex >= currentResults.length) {
      return;
    }

    const updatedResults = [...currentResults];
    const [movedItem] = updatedResults.splice(fromIndex, 1);
    updatedResults.splice(toIndex, 0, movedItem);

    setValue("results", updatedResults, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(data);
  };

  if (isErrorProfileData) {
    console.error(isErrorProfileData);
    return <div>Error loading profile data</div>;
  }

  if (isLoadingProfileData) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />
      <PristineInvalidAlert {...pristineAlert} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ProfileEditHeader
          title="Coach Profile"
          description="Public-facing information about you and your coaching practice."
          form={formState}
          username={coach.username}
        />

        <input type="hidden" {...register("id")} />
        <input type="hidden" {...register("coachId")} />

        <Card>
          <CardHeader>
            <CardTitle>Profile Visibility</CardTitle>
            <CardDescription>
              Control whether your profile is visible to the public. When
              enabled, your profile will be indexed by search engines like
              Google.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormCheckboxField control={control} name="isPublic">
              Make my profile public
            </FormCheckboxField>
          </CardContent>
        </Card>

        {hasFeature(coach, Feature.FancyProfile) && (
          <Card>
            <CardHeader>
              <CardTitle>Your Brand</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <label>Brand Name</label>
                <Input
                  {...register("brandName")}
                  placeholder="Enter your brand name"
                />
                {errors.brandName && (
                  <p className="text-red-500 text-sm">
                    {errors.brandName.message}
                  </p>
                )}
              </div>

              <FormFileInputField
                control={control}
                name="brandLogoUrl"
                label="Brand Logo"
                placeholder="Upload your brand logo"
                recommendedDimensions={{ width: 96, height: 96 }}
                accept=".jpg,.jpeg"
              />
            </CardContent>
          </Card>
        )}

        {hasFeature(coach, Feature.FancyProfile) && (
          <Card>
            <CardHeader>
              <CardTitle>Hero Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormFileInputField
                control={control}
                name="heroImageSrc"
                label="Hero Image"
                helpText="A horizontal image above profile. On mobile, cropped left and right."
                recommendedDimensions={{ width: 2048, height: 480 }}
                accept=".jpg,.jpeg"
              />
            </CardContent>
          </Card>
        )}

        {/* Profile Header */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Header</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 md:flex-row">
            <div className="flex flex-col gap-4 md:w-1/3">
              <FormFileInputField
                control={control}
                name="profilePictureUrl"
                label="Profile Picture URL"
                required
                helpText="Your professional headshot (square format)."
                recommendedDimensions={{ width: 400, height: 400 }}
                accept=".jpg,.jpeg"
              />
            </div>

            <div className="flex flex-col gap-4 md:w-2/3">
              <h1 className="font-heading text-2xl">{coach.fullName}</h1>

              <div>
                <label>Coaching Title *</label>
                <p className="text-sm text-gray-500">
                  What's your title? eg., "Life Coach".
                </p>
                <Input
                  {...register("coachingTitle")}
                  placeholder="Life Coach"
                />
                {errors.coachingTitle && (
                  <p className="text-red-500 text-sm">
                    {errors.coachingTitle.message}
                  </p>
                )}
              </div>

              <div>
                <label>Opening Statement / Quote *</label>
                <p className="text-sm text-gray-500">
                  A short sentence that appears as a quote below your name.
                </p>
                <Input {...register("openingStatement")} />
                {errors.openingStatement && (
                  <p className="text-red-500 text-sm">
                    {errors.openingStatement.message}
                  </p>
                )}
              </div>

              <div>
                <label>Call-to-Action Button Text</label>
                <p className="text-sm text-gray-500">
                  The text shown on the button that leads to your booking page
                </p>
                <Input
                  {...register("ctaButtonText")}
                  placeholder="Book a discovery call"
                />
                {errors.ctaButtonText && (
                  <p className="text-red-500 text-sm">
                    {errors.ctaButtonText.message}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About Me */}
        <Card>
          <CardHeader>
            <CardTitle>About Me</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 md:flex-row">
            {hasFeature(coach, Feature.FancyProfile) && (
              <div className="md:flex-grow-1 flex flex-col gap-4">
                <FormFileInputField
                  control={control}
                  name="mainImage"
                  label="Main Image"
                  helpText="A vertical (2:3) image that appears next to your introduction, ideally a picture of you at work. Note: If you add a video below, it will be shown instead of this image."
                  recommendedDimensions={{ width: 512, height: 768 }}
                  accept=".jpg,.jpeg"
                />

                <div>
                  <label>Main Video</label>
                  <p className="text-sm text-gray-500">
                    A vertical video URL (e.g., from YouTube or Vimeo) that will
                    appear instead of the main image. The video should have a
                    vertical aspect ratio for best display.
                  </p>
                  <Input
                    {...register("mainVideo")}
                    type="url"
                    placeholder="https://..."
                  />
                  {errors.mainVideo && (
                    <p className="text-red-500 text-sm">
                      {errors.mainVideo.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="md:flex-grow-2 flex flex-col gap-4">
              <div>
                <label>Coaching Statement</label>
                <p className="text-sm text-gray-500">
                  Introduce the experience of coaching with you. This is your
                  time to shine! Use this format: I help (your ideal target
                  client) to (achieve their ideal outcome or the journey from
                  pain to outcome).
                </p>
                <ControlledTextarea
                  control={control}
                  name={"title"}
                  className="font-heading text-2xl"
                />
                <p className="text-sm text-gray-500">
                  Use this format:{" "}
                  <em>
                    I help <strong>(your ideal target client)</strong> to{" "}
                    <strong>
                      (achieve their ideal outcome or the journey from pain to
                      outcome)
                    </strong>
                  </em>
                  .
                </p>
                {errors.title && (
                  <p className="text-red-500 text-sm">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label>Description</label>
                <p className="text-sm text-gray-500">
                  Give a longer description of who you are and what you do.
                </p>
                <ControlledTextarea
                  control={control}
                  name={"description"}
                  className="min-h-[200px]"
                  maxLength={400}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm">
                    {errors.description.message}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Magnet */}
        <Card>
          <CardHeader>
            <CardTitle>Lead Magnet</CardTitle>
            <CardDescription>
              Get the contact information of people interested in your work by
              offering something valuable they'll get for free in exchange for
              their contact information.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label>Title</label>
              <p className="text-sm text-gray-500">
                This will show up as the main text. In a few words, explain the
                benefit they'll gain by getting your lead magnet.
              </p>
              <Input
                {...register("leadMagnetTitle")}
                placeholder="3 Keys To A Successful Coaching Business"
              />
              {errors.leadMagnetTitle && (
                <p className="text-red-500 text-sm">
                  {errors.leadMagnetTitle.message}
                </p>
              )}
            </div>

            <div>
              <label>Description (optional)</label>
              <p className="text-sm text-gray-500">
                An additional short sentence that explains more about the lead
                magnet.
              </p>
              <Input
                {...register("leadMagnetDescription")}
                placeholder="Download the free report and checklist now"
              />
              {errors.leadMagnetDescription && (
                <p className="text-red-500 text-sm">
                  {errors.leadMagnetDescription.message}
                </p>
              )}
            </div>

            <div>
              <label>Call-to-Action Text (optional)</label>
              <p className="text-sm text-gray-500">
                The text shown on the button to submit the form. Make this
                action-focused.
              </p>
              <Input
                {...register("leadMagnetCtaText")}
                placeholder="Get Instant Access"
              />
              {errors.leadMagnetCtaText && (
                <p className="text-red-500 text-sm">
                  {errors.leadMagnetCtaText.message}
                </p>
              )}
            </div>

            <div>
              <label>URL</label>
              <p className="text-sm text-gray-500">
                This is the URL prospects will go to after submitting their
                information. Can be a website or a file to download.
              </p>
              <Input {...register("leadMagnetUrl")} placeholder="https://..." />
              {errors.leadMagnetUrl && (
                <p className="text-red-500 text-sm">
                  {errors.leadMagnetUrl.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar Information */}
        <Card>
          <CardHeader>
            <CardTitle>Sidebar Information</CardTitle>
            <CardDescription>
              Give some details about how you coach
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label>Languages *</label>
              <p className="text-sm text-gray-500">
                Languages in which you can coach.
              </p>
              <Input {...register("languages")} />
              {errors.languages && (
                <p className="text-red-500 text-sm">
                  {errors.languages.message}
                </p>
              )}
            </div>

            <FormSelectField
              control={control}
              name="coachingStyle"
              label="Coaching Style"
              required
              helpText="Describe your coaching style in a few words."
              options={[
                "Direct and action-oriented",
                "Structured and goal-focused",
                "Supportive and encouraging",
                "Insightful and reflective",
              ]}
            />

            <FormSelectField
              control={control}
              name="formOfConduct"
              label="Form of Conduct"
              required
              helpText="How do you coach? Online, in-person, hybrid?"
              options={["Online", "In-person", "Hybrid"]}
            />
          </CardContent>
        </Card>

        {/* My Why */}
        <Card>
          <CardHeader>
            <CardTitle>My Why</CardTitle>
            <p className="text-gray-500">
              Share your personal story and motivation behind what you do
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label>Title</label>
              <Input
                {...register("whyTitle")}
                className="font-heading text-2xl"
                placeholder="eg., To help 1 million people..."
              />
              {errors.whyTitle && (
                <p className="text-red-500 text-sm">
                  {errors.whyTitle.message}
                </p>
              )}
            </div>

            <div>
              <label>Your Story</label>
              <ControlledTextarea
                control={control}
                name={"whyContent"}
                placeholder="Share your personal journey and what motivates you to help others..."
                rows={6}
                maxLength={500}
              />
              {errors.whyContent && (
                <p className="text-red-500 text-sm">
                  {errors.whyContent.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>My Main Offer</CardTitle>
          </CardHeader>
          <CardContent>
            In this section, we will show your main offer. If you want to edit
            it,{" "}
            <Link
              className="text-brand-700 hover:text-brand-900 underline hover:no-underline"
              to="/coach/edit-profile/offer"
            >
              click here
            </Link>
            .
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Results I Help My Clients Achieve</CardTitle>
            <p className="text-gray-500">
              What specific results do you help your clients with? These will be
              shown prominently on your profile (in three columns).
            </p>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" onClick={addResult}>
              <Plus /> Add Result
            </Button>

            <div className="mt-4 grid grid-cols-2 gap-4">
              {results.map((result, index) => {
                const resultKey =
                  result.id || result.tempId || `result-${index}`;
                return (
                  <div
                    key={resultKey}
                    className={`flex flex-col gap-2 rounded-lg border border-gray-300 p-4 ${
                      result._delete ? "opacity-50" : ""
                    }`}
                  >
                    <input type="hidden" {...register(`results.${index}.id`)} />

                    <div className="flex w-full items-center justify-between gap-2">
                      <FormCheckboxField
                        control={control}
                        name={`results.${index}._delete` as FieldPath<FormData>}
                      >
                        Delete
                      </FormCheckboxField>
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveResult(index, index - 1)}
                          disabled={0 === index}
                          title="Move Up"
                        >
                          <ArrowUp className="size-[1em]" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => moveResult(index, index + 1)}
                          disabled={index === results.length - 1}
                          title="Move Down"
                        >
                          <ArrowDown className="size-[1em]" />
                        </Button>
                      </div>
                    </div>

                    <FormIconPickerField
                      control={control}
                      name={`results.${index}.icon` as FieldPath<FormData>}
                      label="Icon"
                      required
                      helpText="Pick an icon"
                    />

                    <div>
                      <label>Title *</label>
                      <p className="text-sm text-gray-500">
                        A short title for the result
                      </p>
                      <Input
                        {...register(`results.${index}.title`)}
                        className="font-heading text-lg"
                      />
                      {errors.results?.[index]?.title && (
                        <p className="text-red-500 text-sm">
                          {errors.results[index]?.title?.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label>Description</label>
                      <p className="text-sm text-gray-500">
                        Give a short description of the result
                      </p>
                      <ControlledTextarea
                        control={control}
                        name={
                          `results.${index}.description` as FieldPath<FormData>
                        }
                        maxLength={120}
                      />
                      {errors.results?.[index]?.description && (
                        <p className="text-red-500 text-sm">
                          {errors.results[index]?.description?.message}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {errors.results && (
              <p className="text-red-500 text-sm">{errors.results.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Link to certificates */}
        <Card>
          <CardHeader>
            <CardTitle>Certificates</CardTitle>
          </CardHeader>
          <CardContent>
            Here, we will show your certificates. If you want to edit them,{" "}
            <Link
              className="text-brand-700 hover:text-brand-900 underline hover:no-underline"
              to="/coach/edit-profile/qualifications"
            >
              click here
            </Link>
            .
          </CardContent>
        </Card>

        {/* Link to testimonials */}
        <Card>
          <CardHeader>
            <CardTitle>Testimonials</CardTitle>
          </CardHeader>
          <CardContent>
            Here, we will show your testimonials. If you want to edit them,{" "}
            <Link
              className="text-brand-700 hover:text-brand-900 underline hover:no-underline"
              to="/coach/edit-profile/offer"
            >
              click here
            </Link>{" "}
            and edit them on your offer page.
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle>Social Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label>Website</label>
              <p className="text-sm text-gray-500">
                Your personal or professional website
              </p>
              <Input {...register("website")} />
              {errors.website && (
                <p className="text-red-500 text-sm">{errors.website.message}</p>
              )}
            </div>

            <div>
              <label>Facebook Profile</label>
              <p className="text-sm text-gray-500">Your Facebook profile URL</p>
              <Input {...register("facebookProfile")} />
              {errors.facebookProfile && (
                <p className="text-red-500 text-sm">
                  {errors.facebookProfile.message}
                </p>
              )}
            </div>

            <div>
              <label>LinkedIn Profile</label>
              <p className="text-sm text-gray-500">Your LinkedIn profile URL</p>
              <Input {...register("linkedinProfile")} />
              {errors.linkedinProfile && (
                <p className="text-red-500 text-sm">
                  {errors.linkedinProfile.message}
                </p>
              )}
            </div>

            <div>
              <label>Instagram Profile</label>
              <p className="text-sm text-gray-500">
                Your Instagram profile URL
              </p>
              <Input {...register("instagramProfile")} />
              {errors.instagramProfile && (
                <p className="text-red-500 text-sm">
                  {errors.instagramProfile.message}
                </p>
              )}
            </div>

            <div>
              <label>YouTube Channel</label>
              <p className="text-sm text-gray-500">Your YouTube channel URL</p>
              <Input {...register("youtubeChannel")} />
              {errors.youtubeChannel && (
                <p className="text-red-500 text-sm">
                  {errors.youtubeChannel.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {!hasFeature(coach, Feature.FancyProfile) && (
          <CTASection
            title="Bring your profile to the next level"
            description="On to the Pro Plan, you get an upgraded profile page that shows more of your coaching expertise and value. Plus, you get a host of additional marketing tools in your CRM system."
            link="/coach/membership-plans"
            linkText="View Membership Plans"
            track={{
              medium: TrackingMedium.Cta,
              campaign: TrackingCampaign.ProUpgrade,
            }}
          />
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Profile"}
          </Button>
        </div>
      </form>
    </>
  );
}
